import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { AlertTriangle, Database, Loader2, PlayCircle, Search, ShieldCheck, ListTodo, WandSparkles } from 'lucide-react';

type TransferEntity = 'jobs' | 'customers' | 'ar_invoices' | 'ap_bills';

interface SourceItem {
  profileId: string;
  profileCode: string;
  profileName: string;
  erpType: string;
  sourceKey: string;
  schemaProfile: string;
  lastSyncAt: string | null;
  lastSyncStatus: string | null;
}

interface DiscoverRecord {
  _sourceId: string;
  _sourceTable: string;
  [key: string]: unknown;
}

interface DiscoverResponse {
  sourceKey: string;
  schemaProfile: string;
  entity: TransferEntity;
  totalFound: number;
  returned: number;
  preview: DiscoverRecord[];
}

interface PreviewResponse {
  sourceKey: string;
  schemaProfile: string;
  entity: TransferEntity;
  mappedRecords: Array<Record<string, unknown>>;
  summary: Record<string, unknown>;
}

interface ImportResponse {
  dryRun: boolean;
  syncRun: Record<string, unknown>;
  preview?: Record<string, unknown>;
  summary?: Record<string, unknown>;
  errors?: Array<Record<string, unknown>>;
}

interface TransferTaskRecord {
  id: string;
  title: string | null;
  summary: string | null;
  status: string;
  entity_type: string | null;
  latest_sync_run_id?: string | null;
  input_data?: Record<string, unknown>;
  output_data?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}

interface TaskListResponse {
  items: TransferTaskRecord[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

interface TaskDetailResponse {
  task: TransferTaskRecord;
  syncRun: Record<string, unknown> | null;
}

const ENTITY_OPTIONS: Array<{ value: TransferEntity; label: string }> = [
  { value: 'jobs', label: 'Jobs / Shipments' },
  { value: 'customers', label: 'Customers / Parties' },
  { value: 'ar_invoices', label: 'AR Invoices' },
  { value: 'ap_bills', label: 'AP Bills' },
];

export default function LegacyDataTransferPage() {
  const { user } = useAuth();
  const canRun = user?.role === 'admin' || user?.role === 'platform_admin';
  const [searchParams, setSearchParams] = useSearchParams();

  const [sources, setSources] = useState<SourceItem[]>([]);
  const [tasks, setTasks] = useState<TransferTaskRecord[]>([]);
  const [activeTask, setActiveTask] = useState<TransferTaskRecord | null>(null);
  const [activeTaskSyncRun, setActiveTaskSyncRun] = useState<Record<string, unknown> | null>(null);
  const [selectedProfileId, setSelectedProfileId] = useState<string>('');
  const [entity, setEntity] = useState<TransferEntity>('jobs');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [moduleCode, setModuleCode] = useState('');
  const [jobNo, setJobNo] = useState('');
  const [partyName, setPartyName] = useState('');
  const [status, setStatus] = useState('');
  const [discoverData, setDiscoverData] = useState<DiscoverResponse | null>(null);
  const [previewData, setPreviewData] = useState<PreviewResponse | null>(null);
  const [importResult, setImportResult] = useState<ImportResponse | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [busy, setBusy] = useState<
    'sources' | 'tasks' | 'taskDetail' | 'createTask' | 'updateTask' | 'prepareTask' | 'executeTask' | 'discover' | 'preview' | null
  >(null);

  const selectedSource = useMemo(
    () => sources.find((item) => item.profileId === selectedProfileId) ?? null,
    [sources, selectedProfileId],
  );
  const queryTaskId = searchParams.get('taskId');

  const discoverPayload = useMemo(() => ({
    profileId: selectedProfileId || undefined,
    entity,
    where: {
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      module: moduleCode || undefined,
      jobNo: jobNo || undefined,
      name: partyName || undefined,
      status: status || undefined,
    },
    limit: 50,
  }), [dateFrom, dateTo, entity, jobNo, moduleCode, partyName, selectedProfileId, status]);

  const loadSources = useCallback(async () => {
    if (!canRun) return;
    setBusy('sources');
    try {
      const data = await api.get<{ items: SourceItem[]; total: number }>('/legacy/data-transfer/sources');
      setSources(data.items ?? []);
      setSelectedProfileId((current) => current || data.items?.[0]?.profileId || '');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to load data sources');
    } finally {
      setBusy(null);
    }
  }, [canRun]);

  const applyTaskInput = useCallback((task: TransferTaskRecord) => {
    const input = (task.input_data ?? {}) as Record<string, unknown>;
    setSelectedProfileId(String(input.profileId ?? ''));
    if (typeof input.entity === 'string') {
      setEntity(input.entity as TransferEntity);
    }
    const where = (input.where ?? {}) as Record<string, unknown>;
    setDateFrom(String(where.dateFrom ?? ''));
    setDateTo(String(where.dateTo ?? ''));
    setModuleCode(String(where.module ?? ''));
    setJobNo(String(where.jobNo ?? ''));
    setPartyName(String(where.name ?? ''));
    setStatus(String(where.status ?? ''));
    const sourceIds = Array.isArray(input.sourceIds)
      ? input.sourceIds.map((item) => String(item)).filter(Boolean)
      : [];
    setSelectedIds(sourceIds);
    const output = (task.output_data ?? {}) as Record<string, unknown>;
    const liveResult = output.liveResult as ImportResponse | undefined;
    const dryRunResult = output.dryRunResult as ImportResponse | undefined;
    setImportResult(liveResult ?? dryRunResult ?? null);
  }, []);

  const loadTasks = useCallback(async (focusTaskId?: string | null) => {
    if (!canRun) return;
    setBusy((current) => current ?? 'tasks');
    try {
      const data = await api.get<TaskListResponse>('/legacy/data-transfer/tasks?mine=true&limit=30');
      setTasks(data.items ?? []);
      const effectiveTaskId = focusTaskId ?? queryTaskId;
      if (effectiveTaskId) {
        const matched = data.items.find((item) => item.id === effectiveTaskId);
        if (matched) setActiveTask(matched);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to load transfer tasks');
    } finally {
      setBusy((current) => (current === 'tasks' ? null : current));
    }
  }, [canRun, queryTaskId]);

  const loadTaskDetail = useCallback(async (taskId: string, toastOnLoad = false) => {
    setBusy('taskDetail');
    try {
      const data = await api.get<TaskDetailResponse>(`/legacy/data-transfer/tasks/${taskId}`);
      setActiveTask(data.task);
      setActiveTaskSyncRun(data.syncRun ?? null);
      applyTaskInput(data.task);
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        if (next.get('taskId') !== taskId) next.set('taskId', taskId);
        return next;
      });
      if (toastOnLoad) toast.success('Task loaded');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to load task detail');
    } finally {
      setBusy(null);
    }
  }, [applyTaskInput, setSearchParams]);

  useEffect(() => {
    void loadSources();
  }, [loadSources]);

  useEffect(() => {
    void loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    if (queryTaskId && queryTaskId !== activeTask?.id) {
      void loadTaskDetail(queryTaskId);
    }
  }, [activeTask?.id, loadTaskDetail, queryTaskId]);

  const handleDiscover = useCallback(async () => {
    if (!selectedProfileId) {
      toast.error('Please select a source profile first');
      return;
    }
    setBusy('discover');
    setPreviewData(null);
    setImportResult(null);
    try {
      const data = await api.post<DiscoverResponse>('/legacy/data-transfer/discover', discoverPayload);
      setDiscoverData(data);
      setSelectedIds(data.preview.map((item) => String(item._sourceId)).slice(0, 20));
      toast.success(`Discovered ${data.returned} record(s)`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Discover failed');
    } finally {
      setBusy(null);
    }
  }, [discoverPayload, selectedProfileId]);

  const handlePreview = useCallback(async () => {
    if (!selectedProfileId) {
      toast.error('Please select a source profile first');
      return;
    }
    if (selectedIds.length === 0) {
      toast.error('Please select at least one record');
      return;
    }
    setBusy('preview');
    setImportResult(null);
    try {
      const data = await api.post<PreviewResponse>('/legacy/data-transfer/preview', {
        profileId: selectedProfileId,
        entity,
        sourceIds: selectedIds,
      });
      setPreviewData(data);
      toast.success(`Previewed ${data.mappedRecords.length} mapped record(s)`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Preview failed');
    } finally {
      setBusy(null);
    }
  }, [entity, selectedIds, selectedProfileId]);

  const handleCreateTask = useCallback(async () => {
    if (!selectedProfileId) {
      toast.error('Please select a source profile first');
      return;
    }
    setBusy('createTask');
    try {
      const data = await api.post<{ task: TransferTaskRecord; missingFields: string[] }>('/legacy/data-transfer/tasks', {
        profileId: selectedProfileId,
        entity,
        sourceIds: selectedIds.length > 0 ? selectedIds : undefined,
        where: discoverPayload.where,
        channel: 'manual',
      });
      setActiveTask(data.task);
      setActiveTaskSyncRun(null);
      applyTaskInput(data.task);
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        next.set('taskId', data.task.id);
        return next;
      });
      await loadTasks(data.task.id);
      toast.success(data.missingFields.length > 0 ? 'Task created and waiting for input' : 'Task created');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create task');
    } finally {
      setBusy(null);
    }
  }, [applyTaskInput, discoverPayload.where, entity, loadTasks, selectedIds, selectedProfileId, setSearchParams]);

  const handlePrepareTask = useCallback(async (taskId?: string) => {
    const effectiveTaskId = taskId ?? activeTask?.id;
    if (!effectiveTaskId) {
      toast.error('Please create or open a task first');
      return;
    }
    setBusy('prepareTask');
    try {
      const data = await api.post<{ task: TransferTaskRecord; result?: ImportResponse; missingFields?: string[] }>(
        `/legacy/data-transfer/tasks/${effectiveTaskId}/prepare`,
        {},
      );
      setActiveTask(data.task);
      applyTaskInput(data.task);
      if (data.result) {
        setImportResult(data.result);
        setActiveTaskSyncRun(data.result.syncRun ?? null);
      }
      await loadTasks(effectiveTaskId);
      toast.success(data.missingFields?.length ? 'Task still needs input' : 'Task prepared');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to prepare task');
    } finally {
      setBusy(null);
    }
  }, [activeTask?.id, applyTaskInput, loadTasks]);

  const handleUpdateTask = useCallback(async (taskId?: string) => {
    const effectiveTaskId = taskId ?? activeTask?.id;
    if (!effectiveTaskId) {
      toast.error('Please create or open a task first');
      return;
    }
    setBusy('updateTask');
    try {
      const data = await api.patch<{ task: TransferTaskRecord; missingFields: string[] }>(
        `/legacy/data-transfer/tasks/${effectiveTaskId}/input`,
        {
          profileId: selectedProfileId || undefined,
          entity,
          sourceIds: selectedIds.length > 0 ? selectedIds : undefined,
          where: discoverPayload.where,
        },
      );
      setActiveTask(data.task);
      setActiveTaskSyncRun(null);
      applyTaskInput(data.task);
      setPreviewData(null);
      setImportResult(null);
      await loadTasks(effectiveTaskId);
      toast.success(data.missingFields.length > 0 ? 'Task updated, still waiting for input' : 'Task input updated');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update task input');
    } finally {
      setBusy(null);
    }
  }, [activeTask?.id, applyTaskInput, discoverPayload.where, entity, loadTasks, selectedIds, selectedProfileId]);

  const handleExecuteTask = useCallback(async (taskId?: string) => {
    const effectiveTaskId = taskId ?? activeTask?.id;
    if (!effectiveTaskId) {
      toast.error('Please create or open a task first');
      return;
    }
    setBusy('executeTask');
    try {
      const data = await api.post<{ task: TransferTaskRecord; result: ImportResponse }>(
        `/legacy/data-transfer/tasks/${effectiveTaskId}/execute`,
        {},
      );
      setActiveTask(data.task);
      applyTaskInput(data.task);
      setImportResult(data.result);
      setActiveTaskSyncRun(data.result.syncRun ?? null);
      await loadTasks(effectiveTaskId);
      toast.success('Task executed');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to execute task');
    } finally {
      setBusy(null);
    }
  }, [activeTask?.id, applyTaskInput, loadTasks]);

  function toggleSourceId(sourceId: string, checked: boolean) {
    setSelectedIds((current) => {
      if (checked) return Array.from(new Set([...current, sourceId]));
      return current.filter((item) => item !== sourceId);
    });
  }

  const visibleColumns = useMemo(() => {
    const first = discoverData?.preview?.[0];
    if (!first) return [];
    return Object.keys(first)
      .filter((key) => key !== '_sourceId' && key !== '_sourceTable')
      .slice(0, 5);
  }, [discoverData]);

  if (!canRun) {
    return (
      <div className="p-4">
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Tenant admin required</AlertTitle>
          <AlertDescription>
            This workbench is restricted to tenant admins because it imports legacy operational and financial data into Boox tenant DB.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Legacy Data Transfer</h1>
          <p className="text-sm text-muted-foreground">
            Discover, preview, dry run, and import operational or financial data from bridge sources into the tenant database.
          </p>
        </div>
        <Button variant="outline" onClick={() => void loadSources()} disabled={busy === 'sources'}>
          {busy === 'sources' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Database className="mr-2 h-4 w-4" />}
          Refresh Sources
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Import Scope</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3 xl:grid-cols-6">
          <div className="space-y-2">
            <Label>Source Profile</Label>
            <Select value={selectedProfileId} onValueChange={setSelectedProfileId}>
              <SelectTrigger>
                <SelectValue placeholder="Select source" />
              </SelectTrigger>
              <SelectContent>
                {sources.map((item) => (
                  <SelectItem key={item.profileId} value={item.profileId}>
                    {item.profileName} ({item.schemaProfile})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Entity</Label>
            <Select value={entity} onValueChange={(value) => setEntity(value as TransferEntity)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ENTITY_OPTIONS.map((item) => (
                  <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Date From</Label>
            <Input value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} placeholder="YYYY-MM-DD" />
          </div>
          <div className="space-y-2">
            <Label>Date To</Label>
            <Input value={dateTo} onChange={(e) => setDateTo(e.target.value)} placeholder="YYYY-MM-DD" />
          </div>
          <div className="space-y-2">
            <Label>Module / Job Type</Label>
            <Input value={moduleCode} onChange={(e) => setModuleCode(e.target.value)} placeholder="SI / SE / IMP..." />
          </div>
          <div className="space-y-2">
            <Label>Job No / Ref</Label>
            <Input value={jobNo} onChange={(e) => setJobNo(e.target.value)} placeholder="Optional" />
          </div>
          <div className="space-y-2">
            <Label>Party Name</Label>
            <Input value={partyName} onChange={(e) => setPartyName(e.target.value)} placeholder="Optional" />
          </div>
          <div className="space-y-2">
            <Label>Status</Label>
            <Input value={status} onChange={(e) => setStatus(e.target.value)} placeholder="Optional" />
          </div>
          <div className="flex items-end gap-2 md:col-span-3 xl:col-span-4">
            <Button onClick={() => void handleDiscover()} disabled={busy !== null}>
              {busy === 'discover' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
              Discover
            </Button>
            <Button variant="outline" onClick={() => void handlePreview()} disabled={busy !== null || selectedIds.length === 0}>
              {busy === 'preview' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
              Preview Selected
            </Button>
            <Button variant="secondary" onClick={() => void handleCreateTask()} disabled={busy !== null}>
              {busy === 'createTask' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ListTodo className="mr-2 h-4 w-4" />}
              Create Task
            </Button>
            <Button variant="secondary" onClick={() => void handleUpdateTask()} disabled={busy !== null || !activeTask}>
              {busy === 'updateTask' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ListTodo className="mr-2 h-4 w-4" />}
              Save Task Input
            </Button>
            <Button variant="secondary" onClick={() => void handlePrepareTask()} disabled={busy !== null || !activeTask}>
              {busy === 'prepareTask' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <WandSparkles className="mr-2 h-4 w-4" />}
              Prepare Task
            </Button>
            <Button onClick={() => void handleExecuteTask()} disabled={busy !== null || !activeTask}>
              {busy === 'executeTask' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PlayCircle className="mr-2 h-4 w-4" />}
              Execute Task
            </Button>
          </div>
        </CardContent>
      </Card>

      {selectedSource ? (
        <div className="flex flex-wrap gap-2 text-sm">
          <Badge variant="outline">{selectedSource.profileCode}</Badge>
          <Badge variant="outline">{selectedSource.erpType}</Badge>
          <Badge variant="outline">{selectedSource.schemaProfile}</Badge>
          {selectedSource.lastSyncStatus ? <Badge variant="secondary">{selectedSource.lastSyncStatus}</Badge> : null}
          {activeTask ? <Badge>{activeTask.status}</Badge> : null}
          {activeTask?.title ? <Badge variant="outline">{activeTask.title}</Badge> : null}
        </div>
      ) : null}

      <Tabs defaultValue="tasks" className="space-y-4">
        <TabsList>
          <TabsTrigger value="tasks">Tasks</TabsTrigger>
          <TabsTrigger value="discover">Discover</TabsTrigger>
          <TabsTrigger value="preview">Preview</TabsTrigger>
          <TabsTrigger value="result">Run Result</TabsTrigger>
        </TabsList>

        <TabsContent value="tasks" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>My Transfer Tasks</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {tasks.length === 0 ? (
                <p className="text-sm text-muted-foreground">No transfer tasks yet. Create one from the current import scope.</p>
              ) : (
                <div className="overflow-x-auto rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Entity</TableHead>
                        <TableHead>Updated</TableHead>
                        <TableHead className="w-[280px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tasks.map((task) => (
                        <TableRow key={task.id} data-state={activeTask?.id === task.id ? 'selected' : undefined}>
                          <TableCell>
                            <div className="space-y-1">
                              <p className="font-medium">{task.title ?? task.id}</p>
                              {task.summary ? <p className="text-xs text-muted-foreground">{task.summary}</p> : null}
                            </div>
                          </TableCell>
                          <TableCell><Badge variant="outline">{task.status}</Badge></TableCell>
                          <TableCell>{task.entity_type ?? '—'}</TableCell>
                          <TableCell>{task.updated_at ? new Date(task.updated_at).toLocaleString() : '—'}</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-2">
                              <Button size="sm" variant="outline" onClick={() => void loadTaskDetail(task.id, true)} disabled={busy !== null}>
                                Open
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => void handleUpdateTask(task.id)} disabled={busy !== null}>
                                Save Input
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => void handlePrepareTask(task.id)} disabled={busy !== null}>
                                Prepare
                              </Button>
                              <Button size="sm" onClick={() => void handleExecuteTask(task.id)} disabled={busy !== null}>
                                Execute
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="discover" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Candidate Records</CardTitle>
            </CardHeader>
            <CardContent>
              {!discoverData ? (
                <p className="text-sm text-muted-foreground">Run discover to load candidate records from the selected bridge source.</p>
              ) : (
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                    <span>Source: {discoverData.sourceKey}</span>
                    <span>Profile: {discoverData.schemaProfile}</span>
                    <span>Total Found: {discoverData.totalFound}</span>
                    <span>Returned: {discoverData.returned}</span>
                    <span>Selected: {selectedIds.length}</span>
                  </div>
                  <div className="overflow-x-auto rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">Pick</TableHead>
                          <TableHead>Source ID</TableHead>
                          {visibleColumns.map((column) => (
                            <TableHead key={column}>{column}</TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {discoverData.preview.map((item) => {
                          const sourceId = String(item._sourceId);
                          return (
                            <TableRow key={sourceId}>
                              <TableCell>
                                <Checkbox
                                  checked={selectedIds.includes(sourceId)}
                                  onCheckedChange={(checked) => toggleSourceId(sourceId, Boolean(checked))}
                                />
                              </TableCell>
                              <TableCell className="font-mono text-xs">{sourceId}</TableCell>
                              {visibleColumns.map((column) => (
                                <TableCell key={column} className="max-w-[240px] truncate">
                                  {String(item[column] ?? '—')}
                                </TableCell>
                              ))}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="preview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Mapped Preview</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {!previewData ? (
                <p className="text-sm text-muted-foreground">Select records and run preview to inspect the canonical mapping.</p>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                    <span>Mapped Records: {previewData.mappedRecords.length}</span>
                    {Object.entries(previewData.summary).map(([key, value]) => (
                      <Badge key={key} variant="outline">{key}: {String(value)}</Badge>
                    ))}
                  </div>
                  <div className="max-h-[520px] overflow-auto rounded-md border bg-slate-950 p-3 text-xs text-slate-100">
                    <pre>{JSON.stringify(previewData.mappedRecords.slice(0, 20), null, 2)}</pre>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="result" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Sync Run Result</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {!importResult ? (
                <p className="text-sm text-muted-foreground">Dry run or import to produce a sync run result.</p>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2">
                    {activeTask ? <Badge variant="outline">task: {activeTask.id}</Badge> : null}
                    <Badge>{importResult.dryRun ? 'DRY RUN' : 'LIVE IMPORT'}</Badge>
                    <Badge variant="outline">
                      status: {String(importResult.syncRun.status ?? importResult.syncRun['status'] ?? 'unknown')}
                    </Badge>
                    <Badge variant="outline">
                      run: {String(importResult.syncRun.id ?? importResult.syncRun['id'] ?? '—')}
                    </Badge>
                    {activeTaskSyncRun ? (
                      <Badge variant="outline">
                        linked sync: {String(activeTaskSyncRun.id ?? activeTaskSyncRun['id'] ?? '—')}
                      </Badge>
                    ) : null}
                  </div>
                  {importResult.summary ? (
                    <div className="grid gap-3 md:grid-cols-4">
                      {Object.entries(importResult.summary).map(([key, value]) => (
                        <Card key={key}>
                          <CardContent className="p-4">
                            <p className="text-xs uppercase text-muted-foreground">{key}</p>
                            <p className="mt-1 text-xl font-semibold">{String(value)}</p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : null}
                  {importResult.errors && importResult.errors.length > 0 ? (
                    <div className="max-h-[360px] overflow-auto rounded-md border bg-slate-950 p-3 text-xs text-slate-100">
                      <pre>{JSON.stringify(importResult.errors, null, 2)}</pre>
                    </div>
                  ) : null}
                  {importResult.preview ? (
                    <div className="max-h-[360px] overflow-auto rounded-md border bg-slate-950 p-3 text-xs text-slate-100">
                      <pre>{JSON.stringify(importResult.preview, null, 2)}</pre>
                    </div>
                  ) : null}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
